<template>
  <HeaderC />

  <main class="min-h-screen bg-slate-50 dark:bg-slate-900 pt-24 pb-12 sm:pt-32 sm:pb-20 transition-colors duration-300">
    <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      
      <div class="bg-white dark:bg-slate-800 rounded-3xl shadow-xl ring-1 ring-slate-200 dark:ring-slate-700 overflow-hidden transition-all duration-300">
        
        <div class="relative bg-gradient-to-br from-slate-100 to-white dark:from-slate-800 dark:to-slate-900 p-8 sm:p-12 border-b border-slate-100 dark:border-slate-700">
          <div class="absolute top-0 right-0 p-6 opacity-10">
            <i class="fa-solid fa-shield-halved text-8xl text-slate-900 dark:text-white"></i>
          </div>
          <h1 class="relative text-3xl sm:text-4xl font-black text-slate-900 dark:text-white tracking-tight mb-4">
            {{ t.title }}
          </h1>
          <p class="relative text-sm font-medium text-slate-500 dark:text-slate-400">
            {{ t.updated }} <span class="text-slate-900 dark:text-slate-200">31 Jan 2026</span>
          </p>
        </div>

        <div class="p-8 sm:p-12 prose prose-slate dark:prose-invert max-w-none 
                    prose-headings:font-bold prose-headings:tracking-tight 
                    prose-a:text-blue-600 dark:prose-a:text-blue-400 hover:prose-a:underline
                    prose-li:marker:text-slate-400">
          
          <h3>1. {{ t.introTitle }}</h3>
          <p v-html="t.introText"></p>

          <h3>2. {{ t.respTitle }}</h3>
          <p>{{ t.respText }}</p>
          
          <div class="not-prose my-8 bg-slate-50 dark:bg-slate-900/50 rounded-2xl p-6 border border-slate-200 dark:border-slate-700 flex items-start gap-4">
            <div class="shrink-0 hidden sm:flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
              <i class="fa-solid fa-user-shield text-xl"></i>
            </div>
            <div>
              <p class="text-lg font-bold text-slate-900 dark:text-white">Hugues Narivelo</p>
              <p class="text-sm font-medium text-slate-500 dark:text-slate-400 mb-3">
                {{ t.respRole }}
              </p>
              <div class="flex flex-col sm:flex-row sm:items-center gap-3 text-sm">
                <a href="mailto:contact@hsconseil.ca" class="inline-flex items-center gap-2 text-slate-700 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                  <i class="fa-solid fa-envelope text-slate-400"></i> contact@hsconseil.ca
                </a>
                <span class="hidden sm:inline text-slate-300">|</span>
                <a href="tel:+15145678324" class="inline-flex items-center gap-2 text-slate-700 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                  <i class="fa-solid fa-phone text-slate-400"></i> +1 514 567 8324
                </a>
              </div>
            </div>
          </div>

          <h3>3. {{ t.collectTitle }}</h3>
          <p>{{ t.collectIntro }}</p>
          <ul>
            <li v-html="t.collectList1"></li>
            <li v-html="t.collectList2"></li>
          </ul>

          <h3>4. {{ t.cookiesTitle }}</h3>
          <p v-html="t.cookiesText1"></p>
          <div class="not-prose mt-4 mb-8">
            <button @click="openCookieSettings" class="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-lg text-sm font-semibold hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors cursor-pointer">
              <i class="fa-solid fa-cookie-bite"></i> {{ t.cookiesBtn }}
            </button>
          </div>

          <h3>5. {{ t.destTitle }}</h3>
          <p v-html="t.destText"></p>

          <h3>6. {{ t.rightsTitle }}</h3>
          <p>{{ t.rightsText }}</p>

        </div>
      </div>
    </div>
  </main>

  <FooterC />

  <transition
    enter-active-class="transition ease-out duration-300"
    enter-from-class="opacity-0 translate-y-4"
    enter-to-class="opacity-100 translate-y-0"
    leave-active-class="transition ease-in duration-200"
    leave-from-class="opacity-100 translate-y-0"
    leave-to-class="opacity-0 translate-y-4"
  >
    <button
      v-show="showTop"
      @click="scrollTop"
      class="fixed bottom-6 right-6 z-40 grid place-items-center h-12 w-12 rounded-xl
             text-white bg-gradient-to-br from-blue-600 to-indigo-600 shadow-lg shadow-blue-500/30
             hover:brightness-110 active:scale-95 transition-all duration-300
             dark:ring-1 dark:ring-white/20 cursor-pointer"
      :aria-label="t.backToTop">
      <i class="fa-solid fa-arrow-up text-lg"></i>
    </button>
  </transition>
</template>

<script>
import { defineComponent, computed, ref, onMounted, onBeforeUnmount } from "vue";
import { useStore } from "vuex";
import * as CookieConsent from 'vanilla-cookieconsent';

// IMPORT DES COMPOSANTS GLOBAUX (Ajuste le chemin selon ta structure exacte)
// Si PrivacyPolicy.vue est dans src/views/, le chemin relatif vers components est ../components
import HeaderC from "../components/Visitor_Component/Shared/HighHeader.vue";
import FooterC from "../components/Visitor_Component/Shared/FooterComponent.vue";

export default defineComponent({
  name: "PrivacyPolicy",
  components: { HeaderC, FooterC },
  setup() {
    const store = useStore();
    const showTop = ref(false);

    // --- 1. Gestion de la Langue ---
    const currentLang = computed(() => store.getters.getLangue || "fr");

    // --- 2. Dictionnaire de traduction interne (FR / EN) ---
    // Cela évite de dépendre de fichiers JSON externes pour cette page spécifique
    const content = {
      fr: {
        title: "Politique de confidentialité",
        updated: "Dernière mise à jour :",
        introTitle: "Introduction",
        introText: "<strong>H&S Conseil</strong> (« nous ») prend au sérieux la protection de vos données personnelles. Cette politique explique comment nous collectons, utilisons et protégeons vos informations lorsque vous visitez <strong>hsconseil.ca</strong>, conformément à la Loi 25 du Québec.",
        respTitle: "Responsable de la protection des renseignements personnels",
        respText: "Pour toute question relative à vos données, voici la personne responsable légalement au sein de notre entreprise :",
        respRole: "Président et Consultant Technique SAP",
        collectTitle: "Les données que nous collectons",
        collectIntro: "Nous collectons deux types de données :",
        collectList1: "<strong>Données transmises automatiquement (Témoins/Cookies) :</strong> Votre adresse IP, type de navigateur, pages visitées (via Google Analytics).",
        collectList2: "<strong>Données fournies volontairement :</strong> Votre nom, courriel et téléphone lorsque vous remplissez notre formulaire de contact.",
        cookiesTitle: "Utilisation des témoins (Cookies)",
        cookiesText1: "Nous utilisons <strong>Google Analytics 4</strong> pour comprendre comment nos visiteurs utilisent le site. Ces scripts sont <strong>bloqués par défaut</strong>. Ils ne s'activent que si vous cliquez sur « Tout accepter » dans notre bannière de consentement.",
        cookiesBtn: "Gérer mes préférences de cookies",
        destTitle: "Destination des données",
        destText: "Vos données de contact (formulaire) sont reçues par courriel et stockées dans nos systèmes sécurisés pour répondre à votre demande commerciale. Elles ne sont <strong>jamais vendues</strong> à des tiers.",
        rightsTitle: "Vos droits",
        rightsText: "Vous avez le droit de demander l'accès, la rectification ou la suppression de vos données personnelles. Contactez le responsable mentionné au point 2 pour exercer ce droit.",
        backToTop: "Remonter en haut"
      },
      en: {
        title: "Privacy Policy",
        updated: "Last updated:",
        introTitle: "Introduction",
        introText: "<strong>H&S Conseil</strong> (\"we\") takes the protection of your personal data seriously. This policy explains how we collect, use, and protect your information when you visit <strong>hsconseil.ca</strong>, in compliance with Quebec's Law 25.",
        respTitle: "Person Responsible for Personal Information Protection",
        respText: "For any questions regarding your data, here is the legally responsible person within our company:",
        respRole: "President and SAP Technical Consultant",
        collectTitle: "Data We Collect",
        collectIntro: "We collect two types of data:",
        collectList1: "<strong>Automatically transmitted data (Cookies):</strong> Your IP address, browser type, pages visited (via Google Analytics).",
        collectList2: "<strong>Voluntarily provided data:</strong> Your name, email, and phone number when you fill out our contact form.",
        cookiesTitle: "Use of Cookies",
        cookiesText1: "We use <strong>Google Analytics 4</strong> to understand how our visitors use the site. These scripts are <strong>blocked by default</strong>. They only activate if you click \"Accept All\" in our consent banner.",
        cookiesBtn: "Manage my cookie preferences",
        destTitle: "Data Destination",
        destText: "Your contact data (form) is received by email and stored in our secure systems to respond to your commercial request. It is <strong>never sold</strong> to third parties.",
        rightsTitle: "Your Rights",
        rightsText: "You have the right to request access, rectification, or deletion of your personal data. Contact the responsible person mentioned in section 2 to exercise this right.",
        backToTop: "Back to top"
      }
    };

    // Propriété calculée qui retourne les bons textes
    const t = computed(() => content[currentLang.value] || content.fr);

    // --- 3. Gestion du Scroll (Back to top) ---
    const onScroll = () => { showTop.value = window.scrollY > 420; };

    function scrollTop() {
      const prefersReduced = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      window.scrollTo({ top: 0, behavior: prefersReduced ? "auto" : "smooth" });
    }

    // --- 4. Ouverture Modale Cookies ---
    function openCookieSettings() {
      if(CookieConsent && typeof CookieConsent.showPreferences === 'function'){
         CookieConsent.showPreferences();
      } else if (window.CookieConsent) {
         window.CookieConsent.showPreferences();
      }
    }

    // --- Lifecycle ---
    onMounted(() => {
      window.scrollTo(0, 0);
      window.addEventListener("scroll", onScroll, { passive: true });
      onScroll();
    });

    onBeforeUnmount(() => {
      window.removeEventListener("scroll", onScroll);
    });

    return {
      t,
      showTop,
      scrollTop,
      openCookieSettings
    };
  }
});
</script>

<style scoped>
/* Les styles spécifiques sont gérés par Tailwind Typography (prose) */
</style>